import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PasswordReset() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();

  // State for request form
  const [email, setEmail] = useState("");
  const [requestSent, setRequestSent] = useState(false);
  const [loading, setLoading] = useState(false);

  // State for confirm form
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetDone, setResetDone] = useState(false);

  const handleRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/password-reset-request`, { email });
      setRequestSent(true);
    } catch {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/password-reset-confirm`, { token, new_password: newPassword });
      setResetDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  // Reset confirm form (has token)
  if (token) {
    if (resetDone) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4" data-testid="password-reset-done">
          <Card className="w-full max-w-md rounded-xl border bg-card shadow-lg">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-lime-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-lime-600" />
              </div>
              <h2 className="text-xl font-heading text-slate-900 mb-2">Password Reset</h2>
              <p className="text-sm text-slate-500 mb-6">Your password has been successfully changed.</p>
              <Button onClick={() => navigate("/login")} className="bg-evergreen-950 hover:bg-evergreen-900 text-white w-full">Sign In</Button>
            </CardContent>
          </Card>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4" data-testid="password-reset-confirm">
        <Card className="w-full max-w-md rounded-xl border bg-card shadow-lg">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-heading text-evergreen-950">Set New Password</h1>
              <p className="text-sm text-slate-500 font-accent mt-2">Choose a new password for your account</p>
            </div>
            <form onSubmit={handleConfirm} className="space-y-4">
              <div>
                <Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">New Password</Label>
                <Input data-testid="reset-new-password" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Enter new password" required />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">Confirm Password</Label>
                <Input data-testid="reset-confirm-password" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirm new password" required />
              </div>
              <Button data-testid="reset-submit-btn" type="submit" disabled={loading} className="w-full bg-evergreen-950 hover:bg-evergreen-900 text-white">
                {loading ? "Resetting..." : "Reset Password"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Request form (no token)
  if (requestSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4" data-testid="password-reset-sent">
        <Card className="w-full max-w-md rounded-xl border bg-card shadow-lg">
          <CardContent className="p-8 text-center">
            <h2 className="text-xl font-heading text-slate-900 mb-2">Check Your Email</h2>
            <p className="text-sm text-slate-500 mb-6">If an account exists for <strong>{email}</strong>, we've sent a password reset link.</p>
            <Button variant="outline" onClick={() => navigate("/login")}>Back to Login</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4" data-testid="password-reset-request">
      <Card className="w-full max-w-md rounded-xl border bg-card shadow-lg">
        <CardContent className="p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-heading text-evergreen-950">Reset Password</h1>
            <p className="text-sm text-slate-500 font-accent mt-2">Enter your email to receive a reset link</p>
          </div>
          <form onSubmit={handleRequest} className="space-y-4">
            <div>
              <Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">Email</Label>
              <Input data-testid="reset-email-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" required />
            </div>
            <Button data-testid="reset-request-btn" type="submit" disabled={loading} className="w-full bg-evergreen-950 hover:bg-evergreen-900 text-white">
              {loading ? "Sending..." : "Send Reset Link"}
            </Button>
          </form>
          <p className="text-center text-sm text-slate-500 mt-4">
            <a href="/login" className="text-teal-600 hover:text-teal-700 font-medium">Back to Login</a>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
